// src/Pages/Admin/Dashboard.js
import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../Components/admin/adminLayout";
import { AuthContext } from "../../contextStore/AuthContext";
import client from "../../api/client"; // axios instance

function StatCard({ title, value, sub }) {
  return (
    <div
      style={{
        flex: "1 1 0",
        background: "#fff",
        padding: 16,
        borderRadius: 8,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        minWidth: 160,
      }}
      role="region"
      aria-label={title}
    >
      <div style={{ fontSize: 14, color: "#6b7280" }}>{title}</div>
      <div style={{ fontSize: 28, fontWeight: 700, marginTop: 8 }}>{value}</div>
      {sub ? <div style={{ marginTop: 6, color: "#9ca3af" }}>{sub}</div> : null}
    </div>
  );
}

export default function Dashboard() {
  const { token } = useContext(AuthContext) || {};
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Prefer admin_token if present in localStorage, otherwise fallback to user token
  function getAuthHeaders() {
    try {
      const adminTokenRaw = localStorage.getItem("admin_token") || localStorage.getItem("adminToken");
      const adminToken = adminTokenRaw ? String(adminTokenRaw).trim() : null;
      if (adminToken && adminToken !== "null" && adminToken !== "undefined") {
        return { Authorization: `Bearer ${adminToken}` };
      }
    } catch (e) {
      // ignore localStorage read errors
    }
    // fallback to token from AuthContext
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line
  }, []);

  async function loadStats() {
    setLoading(true);
    setError(null);
    try {
      // Use canonical API path: /api/admin/stats
      const res = await client.get("/api/admin/stats", {

      });

      const data = res?.data ?? {};
      if (data.stats && typeof data.stats === "object") {
        setStats(data.stats);
      } else {
        setStats(data);
      }
    } catch (err) {
      console.error("Failed to load admin stats:", err);

      const status = err?.response?.status;
      // If unauthorized/forbidden, redirect to admin login to re-authenticate
      if (status === 401 || status === 403) {
        // optional: clear stored admin session to force fresh login
        try {
          localStorage.removeItem("admin_token");
          localStorage.removeItem("admin_user");
        } catch (e) { }
        navigate("/admin/login");
        return;
      }

      setError((err.response && (err.response.data?.error || err.response.data?.message)) || err.message || "Failed to load stats");
      setStats(null);
    } finally {
      setLoading(false);
    }
  }

  // helpers for reading various possible key names
  const numberOrDash = (v) => {
    if (v === undefined || v === null) return "—";
    const n = Number(v);
    return Number.isFinite(n) ? n.toLocaleString() : String(v);
  };

  const currencyOrDash = (v) => {
    if (v === undefined || v === null) return "—";
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return `₹ ${n.toLocaleString()}`;
  };

  return (
    <AdminLayout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Admin Dashboard</h2>
        <div style={{ color: "#6b7280" }}>
          {loading ? "Loading…" : `Last refreshed: ${new Date().toLocaleString()}`}
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 12, padding: 12, background: "#fff", borderRadius: 8, color: "#b91c1c" }}>
          <strong>Could not load stats:</strong> {error}
          <div style={{ marginTop: 8 }}>
            <button onClick={loadStats}>Retry</button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <StatCard
          title="Total Users"
          value={stats ? numberOrDash(stats.totalUsers ?? stats.total_users ?? stats.users_total) : "—"}
          sub={stats ? `${numberOrDash(stats.new_users_last_30_days ?? stats.new_users_30d ?? 0)} new (30d)` : "— new (30d)"}
        />
        <StatCard
          title="Total Listings"
          value={stats ? numberOrDash(stats.totalListings ?? stats.total_listings ?? stats.total_machines ?? stats.totalProducts) : "—"}
          sub={stats ? `${numberOrDash(stats.published_listings ?? stats.published ?? 0)} published` : "— published"}
        />
        <StatCard
          title="Total Payments"
          value={stats ? numberOrDash(stats.totalPayments ?? stats.total_payments ?? stats.totalPurchases) : "—"}
          sub={stats ? `Revenue: ${currencyOrDash(stats.totalRevenue ?? stats.total_revenue ?? stats.totalRevenue30d ?? stats.total_revenue_30d ?? stats.revenue_last_30_days)}` : "Revenue: —"}
        />
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, background: "#fff", padding: 16, borderRadius: 8 }}>
          <h4 style={{ marginTop: 0 }}>Revenue (last 30 days)</h4>
          <div style={{ fontSize: 22, fontWeight: 700 }}>
            {stats ? currencyOrDash(stats.revenue_last_30_days ?? stats.revenue_30d ?? stats.total_revenue_30d ?? 0) : "—"}
          </div>
        </div>

        <div style={{ width: 300, background: "#fff", padding: 16, borderRadius: 8 }}>
          <h4 style={{ marginTop: 0 }}>Quick Actions</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button onClick={() => navigate("/admin/users")}>Manage Users</button>
            <button onClick={() => navigate("/admin/listings")}>Manage Listings</button>
            <button onClick={() => navigate("/admin/plans")}>Manage Plans</button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
